/**
 * Langkah 25 KPI ketepatan waktu + langkah 26 Pareto komplain (ISIAN §4/§6, vonis Owner 19/20 Sep 2026).
 *
 * Owner melihat semua cabang, Kepala Cabang cabangnya, staf dirinya sendiri — penyaringan di backend
 * (`services/kpi.py::lingkup`); layar hanya menampilkan. Target (awal 90 %) dikalibrasi Owner di sini (D7).
 * Pareto (kategori / tahap asal / PIC, kumulatif 80 %) untuk Owner dan Kepala Cabang.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/etams/StatusBadge";
import { toast } from "sonner";
import Hint from "@/components/etams/Hint";

const PARETO_ROLES = ["OWNER", "KEPALA_CABANG"];
const LINGKUP = { SEMUA: "Semua wilayah", REGION: "Wilayah Anda", DIRI: "KPI Anda sendiri" };
// Kode peran → nama peran yang dikenal staf.
const NAMA_PERAN = {
  OWNER: "Owner", KEPALA_CABANG: "Kepala Cabang", CS: "CS", ADMIN_PRODUKSI: "Admin Produksi",
  ADMIN_GUDANG: "Admin Gudang", ADMIN_PURCHASING: "Admin Purchasing", FINANCE: "Finance",
  DESIGNER_EDITOR: "Designer Editor", SPV_DESAINER: "SPV Desainer", PEMANTAU: "Pemantau",
};

function Persen({ blok, target }) {
  if (!blok || blok.persen == null) return <span className="text-muted-foreground">belum ada data</span>;
  return (
    <span className="tabular-nums">
      <StatusBadge tone={blok.persen >= target ? "success" : "danger"}>{blok.persen}%</StatusBadge>
      <span className="ml-2 text-xs text-muted-foreground">{blok.tepat}/{blok.total}</span>
    </span>
  );
}

function TabelPareto({ judul, baris, testId }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="font-medium mb-2">{judul}</div>
      {baris.length === 0 ? <div className="text-sm text-muted-foreground">Tidak ada komplain di rentang ini.</div> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground">
            <th className="py-1">Nilai</th><th className="py-1 text-right">Jumlah</th>
            <th className="py-1 text-right">%</th><th className="py-1 text-right">Kumulatif</th></tr></thead>
          <tbody>
            {baris.map((b) => (
              <tr key={b.nilai} className={`border-t border-border ${b.vital ? "font-medium" : ""}`}>
                <td className="py-1">{b.label}{b.vital && <span className="ml-2 text-xs text-destructive">80% teratas</span>}</td>
                <td className="py-1 text-right tabular-nums">{b.jumlah}</td>
                <td className="py-1 text-right tabular-nums">{b.persen}%</td>
                <td className="py-1 text-right tabular-nums">{b.kumulatif_persen}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export default function KpiKetepatan() {
  const { roleCode, isOwner } = useAuth();
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [data, setData] = useState(null);
  const [pareto, setPareto] = useState(null);
  const [regions, setRegions] = useState({});
  const [targetBaru, setTargetBaru] = useState({});
  const [sibuk, setSibuk] = useState(false);
  const bolehPareto = PARETO_ROLES.includes(roleCode);

  const muat = useCallback(async () => {
    setSibuk(true);
    const params = { dari: dari || undefined, sampai: sampai || undefined };
    try {
      const [k, p] = await Promise.all([
        api.get("/kpi", { params }),
        bolehPareto ? api.get("/kpi/pareto-komplain", { params }) : Promise.resolve(null),
      ]);
      setData(k.data);
      setPareto(p ? p.data : null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "KPI gagal dimuat"));
    } finally {
      setSibuk(false);
    }
  }, [dari, sampai, bolehPareto]);
  useEffect(() => { muat(); }, [muat]);
  useEffect(() => {
    api.get("/regions").then((r) => {
      const peta = {};
      (r.data?.items || r.data || []).forEach((x) => { peta[x.id] = x.name || x.code; });
      setRegions(peta);
    }).catch((e) => toast.error(apiErrorMessage(e, "Nama cabang gagal dimuat.")));
  }, []);

  const simpanTarget = async (kode) => {
    const nilai = Number(targetBaru[kode]);
    if (Number.isNaN(nilai) || nilai < 0 || nilai > 100) { toast.error("Target 0–100"); return; }
    try {
      await api.patch(`/kpi/definisi/${kode}`, { target_pct: nilai });
      toast.success("Target dikalibrasi");
      setTargetBaru({ ...targetBaru, [kode]: "" });
      muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal menyimpan target")); }
  };
  const namaCabang = (id) => regions[id] || "—";

  return (
    <div className="max-w-5xl space-y-6" data-testid="kpi-page">
      <div>
        <h1 className="text-2xl font-semibold">
          KPI Ketepatan Waktu{" "}
          <Hint>Dihitung dari jejak sistem (hari kerja; tutup hari 17.00 WIB / 18.00 WITA). Tindakan atas nama orang lain tidak dihitung.</Hint>
        </h1>
        {data && (
          <p className="mt-1 text-sm text-muted-foreground">
            {LINGKUP[data.lingkup]} · {data.dari} s.d. {data.sampai}
          </p>
        )}
      </div>

      <Card className="p-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">Dari<Input type="date" value={dari} onChange={(e) => setDari(e.target.value)} data-testid="kpi-dari" /></label>
        <label className="text-sm">Sampai<Input type="date" value={sampai} onChange={(e) => setSampai(e.target.value)} data-testid="kpi-sampai" /></label>
        <Button variant="outline" disabled={sibuk} onClick={muat}>Muat ulang</Button>
        <span className="text-xs text-muted-foreground">Kosong = bulan berjalan.</span>
      </Card>

      {(data?.kpi || []).map((k) => (
        <Card key={k.kode} className="p-4 space-y-2" data-testid={`kpi-${k.kode}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-medium">{k.nama}</div>
              <div className="text-xs text-muted-foreground">{k.ukuran} · target {k.target_pct}%</div>
            </div>
            <Persen blok={k.keseluruhan} target={k.target_pct} />
          </div>
          {data.lingkup !== "DIRI" && k.per_cabang.length > 0 && (
            <div className="text-sm grid gap-1">
              {k.per_cabang.map((c) => (
                <div key={c.region_id} className="flex justify-between border-t border-border pt-1">
                  <span>{namaCabang(c.region_id)}</span><Persen blok={c} target={k.target_pct} />
                </div>
              ))}
              {k.per_peran.map((c) => (
                <div key={`${c.region_id}-${c.peran}`} className="flex justify-between text-xs text-muted-foreground">
                  <span className="pl-4">{namaCabang(c.region_id)} · {NAMA_PERAN[c.peran] || "—"}</span><Persen blok={c} target={k.target_pct} />
                </div>
              ))}
            </div>
          )}
          {isOwner && (
            <div className="flex items-center gap-2 pt-1">
              <Input className="w-28" type="number" min={0} max={100} placeholder={String(k.target_pct)}
                     value={targetBaru[k.kode] ?? ""} onChange={(e) => setTargetBaru({ ...targetBaru, [k.kode]: e.target.value })}
                     data-testid={`kpi-target-${k.kode}`} />
              <Button size="sm" variant="outline" disabled={!targetBaru[k.kode]} onClick={() => simpanTarget(k.kode)}>Kalibrasi target</Button>
            </div>
          )}
        </Card>
      ))}

      {bolehPareto && pareto && (
        <div className="space-y-3" data-testid="pareto-komplain">
          <h2 className="text-lg font-semibold">Pareto komplain ({pareto.total})</h2>
          {pareto.belum_diisi_tahap > 0 && (
            <p className="text-xs text-muted-foreground">{pareto.belum_diisi_tahap} komplain belum diisi tahap asal — isi di layar Komplain.</p>
          )}
          <TabelPareto judul="Per kategori" baris={pareto.kategori} testId="pareto-kategori" />
          <TabelPareto judul="Per tahap asal" baris={pareto.tahap} testId="pareto-tahap" />
          <TabelPareto judul="Per PIC" baris={pareto.pic} testId="pareto-pic" />
        </div>
      )}
    </div>
  );
}
